import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Tooltip } from '@mui/material';
import { PackageOrder, PackageCustomer, PhoneCall } from '../types';
import { RightColumn, ActionButton } from '../styles/StyledComponents';

// ============================================================================
// RIGHT PANEL COMPONENT - Sağ Sütun (İşlemler / Butonlar)
// ============================================================================

export interface ActiveOrderInfo {
  hasActiveOrder: boolean;
  activeOrderForAddress: PackageOrder | null | undefined;
  canCreateNewOrder: boolean;
  canAddToOrder: boolean;
  isSameAddress: boolean;
}

export interface StatusButtonInfo {
  label: string;
  emoji: string;
  color: string;
  nextStatus: string | null;
  disabled: boolean;
}

export interface RightPanelProps {
  // Customer & Call state
  currentCustomer: PackageCustomer | null;
  incomingCall: PhoneCall | null;
  isCallAnswered: boolean;
  selectedAddressId: number | null;
  
  // Order state
  selectedOrderId: number | null;
  selectedOrder: PackageOrder | null;
  activeOrderInfo: ActiveOrderInfo;
  getStatusButtonInfo: StatusButtonInfo;
  
  // Screen settings
  useCashierSystem: boolean;
  currentCashier: { cashierId: number; cashierName: string } | null;
  
  // Handlers
  onOpenQuickSale: () => void;
  onOpenAddOnOrder: (orderId: number) => void; // ✅ YENİ: İlave sipariş modu
  onOpenManualPhone: () => void;
  onOpenCashierLogin: () => void;
  onOpenDetail: () => void;
  onOpenPaymentModal: () => void;
  onKitchenPrint: (orderId: number, source?: string) => void;
  onUpdateOrderStatus: (orderId: number, status: string) => void;
  onOpenCourierModal: () => void;
  onRejectMigrosOrder?: (orderId: number) => void;
  onRejectYsOrder?: (orderId: number) => void;
  onRejectTrendyolOrder?: (orderId: number) => void;
  
  // Platform kapatma
  selectedTab: string;
  platformStatuses: { getirOpen: boolean; migrosOpen: boolean; ysOpen: boolean; trendyolOpen: boolean };
  onCloseRestaurant: () => void;
  
  // Ödeme kontrol
  onPaymentVerification: () => void;
  
  // Theme
  themeColors: { primary: string; secondary: string };
}

const RightPanel: React.FC<RightPanelProps> = ({
  currentCustomer,
  incomingCall,
  isCallAnswered,
  selectedAddressId,
  selectedOrderId,
  selectedOrder,
  activeOrderInfo,
  getStatusButtonInfo,
  useCashierSystem,
  currentCashier,
  onOpenQuickSale,
  onOpenAddOnOrder, // ✅ YENİ: İlave sipariş modu
  onOpenManualPhone,
  onOpenCashierLogin,
  onOpenDetail,
  onOpenPaymentModal,
  onKitchenPrint,
  onUpdateOrderStatus,
  onOpenCourierModal,
  onRejectMigrosOrder,
  onRejectYsOrder,
  onRejectTrendyolOrder,
  selectedTab,
  platformStatuses,
  onCloseRestaurant,
  onPaymentVerification,
  themeColors,
}) => {
  const { t } = useTranslation('salesSystem');
  // Yeni Sipariş butonu için hesaplamalar
  const baseConditionNew = (isCallAnswered || (currentCustomer && !incomingCall)) && selectedAddressId;
  const canCreateOrder = baseConditionNew && activeOrderInfo.canCreateNewOrder;
  const isCashierRequiredNew = useCashierSystem && !currentCashier;
  const isNewOrderButtonEnabled = canCreateOrder && !isCashierRequiredNew;
  const noCustomerSelected = !currentCustomer && !incomingCall && !isCallAnswered;
  const isNewOrderButtonActive = isNewOrderButtonEnabled || noCustomerSelected;

  // ✅ Yeni Sipariş butonu — neden pasif olduğunu açıklayan dinamik tooltip
  // (Sadece buton pasif iken gösterilir.)
  // NOT: Eski "aynı adrese aktif sipariş var, farklı adres seçin" engeli
  // kaldırıldı (kullanıcı kararı) — canCreateNewOrder artık her zaman true,
  // aynı adres durumu yalnızca buton üzerinde bilgi notu olarak gösterilir.
  const newOrderTooltipTitle = (() => {
    if (isNewOrderButtonActive) return '';
    if (isCashierRequiredNew) {
      return t('orderManagement.rightPanel.cashierLoginRequired', 'Önce kasiyer girişi yapın');
    }
    if (currentCustomer && !selectedAddressId) {
      return t('orderManagement.rightPanel.selectAddressFirst', 'Lütfen önce bir adres seçin');
    }
    return '';
  })();

  // İlave Sipariş butonu için hesaplamalar
  // ✅ Müşteri + adres seçili olmalı, sipariş kuryeye verilmemiş olmalı
  const hasCustomerAndAddress = !!(currentCustomer && selectedAddressId);
  const isSelectedOrderAddable = selectedOrder && 
    (selectedOrder.status === 'new' || selectedOrder.status === 'accepted' || selectedOrder.status === 'preparing' || selectedOrder.status === 'ready') &&
    !selectedOrder.courierName &&
    hasCustomerAndAddress;
  const isCashierRequiredAdd = useCashierSystem && !currentCashier;
  const isAddOrderButtonEnabled = isSelectedOrderAddable && !isCashierRequiredAdd;

  // Ödeme butonu için hesaplamalar
  const isPrepaidSource = selectedOrder?.source === 'qr' || selectedOrder?.source === 'ecommerce' || selectedOrder?.source === 'kiosk';
  const isPlatformOrder = ['getir', 'migros', 'yemeksepeti', 'trendyol'].includes(selectedOrder?.source || '');
  const isPhoneOrder = selectedOrder?.source === 'phone';
  const isYsOnway = selectedOrder?.source === 'yemeksepeti' && selectedOrder?.status === 'onway';
  const isCancelledOrder = selectedOrder?.status === 'cancelled';
  // Telefon siparislerinde siparis teslim edilmeden de odeme alinabilmeli.
  // İptal edilen siparişlerde ödeme alınamaz (cancel sonrası para tahsil edilemez).
  const canTakePayment = selectedOrderId
    && !selectedOrder?.isPaid
    && !isPrepaidSource
    && !isCancelledOrder
    && (
      isPlatformOrder ||
      isPhoneOrder ||
      selectedOrder?.status === 'delivered' ||
      isYsOnway
    );

  // Ödeme Al butonu için dinamik tooltip mesajı (yalnız tıklanamaz durumlarda gösterilir).
  const paymentTooltipTitle = isCancelledOrder
    ? t('orderManagement.rightPanel.cancelledNoPayment', 'İptal edilmiş siparişe ödeme alınamaz')
    : selectedOrder?.isPaid
      ? t('orderManagement.rightPanel.alreadyPaid', 'Bu sipariş zaten ödenmiş')
      : isPrepaidSource
        ? t('orderManagement.rightPanel.prepaidNoPayment', 'Bu kanal peşin ödemelidir')
        : !canTakePayment
          ? t('orderManagement.rightPanel.cannotTakePayment', 'Bu sipariş için şu anda ödeme alınamaz')
          : '';

  return (
    <RightColumn>
      {/* Yeni Sipariş — neden pasif olduğunu açıklayan tooltip ile sarılı */}
      <Tooltip
        title={newOrderTooltipTitle}
        arrow
        disableHoverListener={!newOrderTooltipTitle}
        disableFocusListener={!newOrderTooltipTitle}
        disableTouchListener={!newOrderTooltipTitle}
      >
        {/* MUI Tooltip pointer-events:none child için sarmalayıcı span ister */}
        <span style={{ display: 'block' }}>
          <ActionButton
            onClick={() => {
              if (isCashierRequiredNew) {
                onOpenCashierLogin();
                return;
              }
              if (noCustomerSelected) {
                onOpenManualPhone();
                return;
              }
              if (!canCreateOrder) return;
              onOpenQuickSale();
            }}
            sx={{
              bgcolor: isNewOrderButtonActive ? themeColors.primary : 'grey.400',
              color: 'white',
              opacity: isNewOrderButtonActive ? 1 : 0.6,
              cursor: isNewOrderButtonActive ? 'pointer' : 'not-allowed',
              width: '100%',
            }}
          >
            <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.newOrder')}</Typography>
            <Typography variant="h6">🛒</Typography>
            {isCashierRequiredNew && (
              <Typography variant="caption" sx={{ fontSize: 8, mt: 0.25 }}>{t('orderManagement.rightPanel.cashierRequired')}</Typography>
            )}
            {noCustomerSelected && (
              <Typography variant="caption" sx={{ fontSize: 8, mt: 0.25 }}>{t('orderManagement.rightPanel.enterPhone')}</Typography>
            )}
            {/* Bilgi notu: aynı adrese kuryeye verilmemiş sipariş var — yeni
                sipariş yine de açılabilir; kasiyer isterse İlave'yi seçer. */}
            {!isCashierRequiredNew && !noCustomerSelected && activeOrderInfo.hasActiveOrder && activeOrderInfo.isSameAddress && (
              <Typography variant="caption" sx={{ fontSize: 8, mt: 0.25, lineHeight: 1.1 }}>
                {t('orderManagement.rightPanel.sameAddressShort', 'Aynı adreste aktif sipariş var')}
              </Typography>
            )}
          </ActionButton>
        </span>
      </Tooltip>

      {/* İlave Sipariş */}
      <ActionButton 
        onClick={() => {
          if (isCashierRequiredAdd) {
            onOpenCashierLogin();
            return;
          }
          if (!isSelectedOrderAddable || !selectedOrderId) return;
          // ✅ Seçili siparişe ilave ürün ekle
          onOpenAddOnOrder(selectedOrderId);
        }}
        sx={{ 
          bgcolor: isAddOrderButtonEnabled ? 'success.main' : 'grey.400', 
          color: 'white',
          opacity: isAddOrderButtonEnabled ? 1 : 0.5,
          cursor: isAddOrderButtonEnabled ? 'pointer' : 'not-allowed',
        }}
      >
        <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.addOnOrder')}</Typography>
        <Typography variant="h6">➕</Typography>
        {isSelectedOrderAddable && selectedOrder && (
          <Typography variant="caption" sx={{ fontSize: 9, mt: 0.5 }}>
            #{selectedOrder.shortOrderNo}
          </Typography>
        )}
      </ActionButton>

      {/* Detay */}
      <ActionButton onClick={onOpenDetail}>
        <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.detail')}</Typography>
        <Typography variant="h6">📋</Typography>
      </ActionButton>

      {/* Migros Reddet/Iptal */}
      {selectedOrder?.source === 'migros' && selectedOrderId && 
       selectedOrder.migrosStatus && 
       ['NEW_PENDING', 'APPROVED'].includes(selectedOrder.migrosStatus.toUpperCase()) && (
        <ActionButton
          onClick={() => onRejectMigrosOrder?.(selectedOrderId)}
          sx={{
            bgcolor: selectedOrder.migrosStatus.toUpperCase() === 'NEW_PENDING' ? '#ff9800' : '#f44336',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          <Typography variant="caption" fontWeight="bold">
            {selectedOrder.migrosStatus.toUpperCase() === 'NEW_PENDING' ? t('orderManagement.rightPanel.reject') : t('orderManagement.rightPanel.cancel')}
          </Typography>
          <Typography variant="h6">&#10060;</Typography>
        </ActionButton>
      )}

      {/* Yemeksepeti Reddet - sadece PENDING (onaylanmamis) siparisler icin */}
      {selectedOrder?.source === 'yemeksepeti' && selectedOrderId && 
       selectedOrder.yemeksepetiStatus && 
       ['PENDING', 'NEW'].includes(selectedOrder.yemeksepetiStatus.toUpperCase()) && (
        <ActionButton
          onClick={() => onRejectYsOrder?.(selectedOrderId)}
          sx={{
            bgcolor: '#e31e24',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.reject')}</Typography>
          <Typography variant="h6">&#10060;</Typography>
        </ActionButton>
      )}

      {/* Trendyol İptal - Created/Picking durumundaki siparisler icin */}
      {selectedOrder?.source === 'trendyol' && selectedOrderId && 
       selectedOrder.trendyolStatus && 
       ['Created', 'Picking'].includes(selectedOrder.trendyolStatus) && (
        <ActionButton
          onClick={() => onRejectTrendyolOrder?.(selectedOrderId)}
          sx={{
            bgcolor: '#f27a1a',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.cancel')}</Typography>
          <Typography variant="h6">&#10060;</Typography>
        </ActionButton>
      )}

      {/* Odeme Al — iptal/ödenmiş/peşin durumlarda tıklama kilitli; sebep tooltip ile */}
      <Tooltip
        title={paymentTooltipTitle}
        arrow
        disableHoverListener={!paymentTooltipTitle}
        disableFocusListener={!paymentTooltipTitle}
        disableTouchListener={!paymentTooltipTitle}
      >
        {/* MUI Tooltip pointer-events:none child için sarmalayıcı span ister */}
        <span style={{ display: 'block' }}>
          <ActionButton
            onClick={canTakePayment ? onOpenPaymentModal : undefined}
            sx={{
              opacity: canTakePayment || isPrepaidSource || selectedOrder?.isPaid ? 1 : 0.5,
              cursor: canTakePayment ? 'pointer' : 'not-allowed',
              pointerEvents: canTakePayment ? 'auto' : 'none',
              bgcolor: isPrepaidSource
                ? 'grey.400'
                : selectedOrder?.isPaid
                  ? 'success.main'
                  : canTakePayment
                    ? 'success.main'
                    : undefined,
              color: canTakePayment || isPrepaidSource || selectedOrder?.isPaid ? 'white' : undefined,
              '&:hover': canTakePayment ? {
                bgcolor: 'success.dark',
              } : undefined,
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {isPrepaidSource ? t('orderManagement.rightPanel.prepaid')
                : selectedOrder?.isPaid ? t('orderManagement.rightPanel.paid')
                : t('orderManagement.rightPanel.takePayment')}
            </Typography>
            <Typography variant="h6">
              {isPrepaidSource ? '✅'
                : selectedOrder?.isPaid ? '✅'
                : '💳'}
            </Typography>
            {isPrepaidSource && (
              <Typography variant="caption" sx={{ fontSize: 8, mt: 0.25 }}>
                {selectedOrder?.source === 'qr' ? t('orderManagement.rightPanel.qrMenuShort') : selectedOrder?.source === 'kiosk' ? t('orderManagement.rightPanel.kioskShort') : t('orderManagement.rightPanel.ecommerceShort')}
              </Typography>
            )}
            {isPlatformOrder && !selectedOrder?.isPaid && (
              <Typography variant="caption" sx={{ fontSize: 8, mt: 0.25 }}>
                {selectedOrder?.source === 'getir' ? t('orderManagement.rightPanel.platformGetir') : selectedOrder?.source === 'migros' ? t('orderManagement.rightPanel.platformMigros') : selectedOrder?.source === 'yemeksepeti' ? t('orderManagement.rightPanel.platformYemeksepeti') : t('orderManagement.rightPanel.platformTrendyol')}
              </Typography>
            )}
          </ActionButton>
        </span>
      </Tooltip>

      {/* Mutfağa Yazdır */}
      <ActionButton 
        onClick={() => selectedOrderId && onKitchenPrint(selectedOrderId, selectedOrder?.source)}
        sx={{ 
          opacity: selectedOrderId ? 1 : 0.5,
          cursor: selectedOrderId ? 'pointer' : 'not-allowed',
        }}
      >
        <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.printKitchen')}</Typography>
        <Typography variant="h6">🖨️</Typography>
      </ActionButton>

      {/* Kontrol Yap */}
      <ActionButton
        onClick={onPaymentVerification}
        sx={{
          cursor: 'pointer',
          bgcolor: '#e3f2fd',
          '&:hover': { bgcolor: '#bbdefb' },
        }}
      >
        <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.verify')}</Typography>
        <Typography variant="h6">🔍</Typography>
      </ActionButton>

      {/* Restoranı Kapat */}
      {(() => {
        const isPlatformTab = ['getir', 'migros', 'yemeksepeti', 'trendyol'].includes(selectedTab);
        const isOpen = (selectedTab === 'getir' && platformStatuses.getirOpen)
          || (selectedTab === 'migros' && platformStatuses.migrosOpen)
          || (selectedTab === 'yemeksepeti' && platformStatuses.ysOpen)
          || (selectedTab === 'trendyol' && platformStatuses.trendyolOpen);
        if (!isPlatformTab || !isOpen) return null;
        return (
          <ActionButton
            onClick={onCloseRestaurant}
            sx={{
              bgcolor: '#d32f2f',
              color: 'white',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            <Typography variant="caption" fontWeight="bold">{t('orderManagement.rightPanel.closeRestaurant')}</Typography>
            <Typography variant="h6">🔒</Typography>
          </ActionButton>
        );
      })()}

      {/* Durum Butonu */}
      <ActionButton 
        onClick={() => {
          if (!getStatusButtonInfo.disabled && selectedOrderId && getStatusButtonInfo.nextStatus) {
            if (getStatusButtonInfo.nextStatus === 'ON_DELIVERY' || getStatusButtonInfo.nextStatus === 'DELIVER_WITH_COURIER' || getStatusButtonInfo.nextStatus === 'MIGROS_ASSIGN_COURIER' || getStatusButtonInfo.nextStatus === 'YS_ASSIGN_COURIER') {
              onOpenCourierModal();
            } else {
              onUpdateOrderStatus(selectedOrderId, getStatusButtonInfo.nextStatus);
            }
          }
        }}
        sx={{ 
          bgcolor: getStatusButtonInfo.disabled ? 'grey.300' : getStatusButtonInfo.color, 
          color: getStatusButtonInfo.disabled ? 'text.disabled' : 'white',
          mt: 'auto',
          cursor: getStatusButtonInfo.disabled ? 'not-allowed' : 'pointer',
          opacity: getStatusButtonInfo.disabled ? 0.7 : 1,
        }}
      >
        <Typography variant="caption" fontWeight="bold">{getStatusButtonInfo.label}</Typography>
        <Typography variant="h6">{getStatusButtonInfo.emoji}</Typography>
      </ActionButton>
    </RightColumn>
  );
};

export default RightPanel;
